'use client'

/* eslint-disable jsx-a11y/anchor-is-valid */
import { handlePrivacyOnClick } from '@meowwolf/cookie-consent-utils'
import Image from 'next/image'
import noop from 'lodash-es/noop'
import { FunctionComponent, useSyncExternalStore } from 'react'
import styles from './Footer.module.css'
import { MAIN_NAV } from '@/config/navigation.config'
import { NEXT_PUBLIC_ZARAZ_CONSENT_ENABLED } from '@/config/flags.config'
import TXT from '@/messages'

const SOCIAL_NAV: Array<{
  label: string
  url: string
  icon: string
}> = [
  {
    label: 'Facebook',
    url: 'https://www.facebook.com/meowwolf.sf',
    icon: '/static/svg/facebook_squared.svg',
  },
  {
    label: 'Instagram',
    url: 'https://www.instagram.com/meow__wolf/',
    icon: '/static/svg/instagram_squared.svg',
  },
  {
    label: 'Twitter',
    url: 'https://twitter.com/meowwolf',
    icon: '/static/svg/twitter_squared.svg',
  },
  {
    label: 'YouTube',
    url: 'https://www.youtube.com/meowwolf',
    icon: '/static/svg/youtube_squared.svg',
  },
]

const subscribeNoop = (): typeof noop => noop

const Footer: FunctionComponent = () => {
  // Server snapshot is false so hydration matches; the client snapshot is applied right after
  const hasZaraz = useSyncExternalStore(
    subscribeNoop, // no subscription needed
    () => Boolean(NEXT_PUBLIC_ZARAZ_CONSENT_ENABLED) && typeof window.zaraz !== 'undefined',
    () => false, // server snapshot
  )

  return (
    <>
      <div className={styles.Swoop}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 130">
          <g id="Layer_2" data-name="Layer 2">
            <g id="Layer_1-2" data-name="Layer 1">
              <path
                fillRule="evenodd"
                className={styles.SwoopPath}
                d="M0,130H1440V22.15c-187,52.06-292.56-32.77-528-21S669,93.28,459,108.61s-304-78.2-459-70Z"
              />
            </g>
          </g>
        </svg>
      </div>

      <footer className={styles.Footer} role="contentinfo">
        <div className={styles.MainNav} role="navigation" aria-label="footer-navigation">
          {MAIN_NAV.map(([text, url]) => (
            <a suppressHydrationWarning href={url} key={text}>
              {text}
            </a>
          ))}
        </div>

        <div className={styles.Social}>
          <h2>{TXT.siteChrome.connectWithUs}</h2>
          <div className={styles.SocialIcons}>
            {SOCIAL_NAV.map(({
              label,
              url,
              icon,
            }) => (
              <a
                suppressHydrationWarning
                aria-label={label}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                key={label}
              >
                <Image
                  aria-hidden
                  src={icon}
                  alt={label}
                  width={24}
                  height={24}
                  unoptimized
                />
              </a>
            ))}
          </div>
        </div>

        <div className={styles.Badges}>
          <a
            aria-label={TXT.eventsA11y.certifiedBCorp}
            href="https://www.bcorporation.net/"
            rel="nofollow noopener noreferrer"
            target="_blank"
          >
            <svg
              height="72px"
              width="49.5px"
              id="logo-bcorp"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 203.77 296.29"
            >
              <path
                className={styles.BCorpSvg}
                d="M45.7 31.47A6 6 0 0 0 44.4 30a6.23 6.23 0 0 0-3.92-1.37 7 7 0 0 0-3.42.78 6.5 6.5 0 0 0-2.28 2.1 9.17 9.17 0 0 0-1.27 3 14.8 14.8 0 0 0-.4 3.46 13.77 13.77 0 0 0 .4 3.33 9 9 0 0 0 1.26 2.9A6.54 6.54 0 0 0 37 46.23a7 7 0 0 0 3.42.78 5.52 5.52 0 0 0 4.28-1.66 7.73 7.73 0 0 0 1.88-4.4h5.78a13.8 13.8 0 0 1-1.18 4.6A11 11 0 0 1 48.72 49a10.7 10.7 0 0 1-3.65 2.2 13.42 13.42 0 0 1-4.6.77 13.92 13.92 0 0 1-5.6-1.1 12 12 0 0 1-4.2-3A13.22 13.22 0 0 1 28 43.46a16.4 16.4 0 0 1-.9-5.53 17 17 0 0 1 .9-5.65 13.55 13.55 0 0 1 2.62-4.54 12 12 0 0 1 4.2-3 14.56 14.56 0 0 1 9.85-.46 11.46 11.46 0 0 1 3.58 1.88 10.17 10.17 0 0 1 2.6 3.06 10.92 10.92 0 0 1 1.3 4.2H46.4a4.9 4.9 0 0 0-.7-1.93zm15.23 15.19a5.54 5.54 0 0 0 6.3.36 3.4 3.4 0 0 0 1.46-1.73h4.75a9 9 0 0 1-3.5 5.05 10.32 10.32 0 0 1-5.7 1.52 11.22 11.22 0 0 1-4.2-.74A8.68 8.68 0 0 1 56.9 49a9.5 9.5 0 0 1-2-3.27 12 12 0 0 1-.7-4.2 11.47 11.47 0 0 1 .7-4.1A9.5 9.5 0 0 1 60.16 32a10.16 10.16 0 0 1 4.1-.8 9.36 9.36 0 0 1 4.36 1 8.74 8.74 0 0 1 3.07 2.6 10.7 10.7 0 0 1 1.73 3.74 13.9 13.9 0 0 1 .38 4.37H59.6a5.45 5.45 0 0 0 1.33 3.76zM67 36.32a3.8 3.8 0 0 0-2.95-1.07 4.72 4.72 0 0 0-2.15.44 4.08 4.08 0 0 0-1.37 1.07 4 4 0 0 0-.72 1.37 6.18 6.18 0 0 0-.24 1.3h8.8A6.23 6.23 0 0 0 67 36.3zm14.35-4.6v3.65h.08a6.64 6.64 0 0 1 1-1.7 6.76 6.76 0 0 1 1.48-1.32 7.27 7.27 0 0 1 1.8-.86 6.45 6.45 0 0 1 2-.3 4.25 4.25 0 0 1 1.18.18v5c-.25-.05-.56-.1-.9-.13a9.66 9.66 0 0 0-1-.06 5.76 5.76 0 0 0-2.52.5 4.33 4.33 0 0 0-1.68 1.3 5.43 5.43 0 0 0-.9 2 10.92 10.92 0 0 0-.26 2.47v8.86h-5.4v-19.6zm21.99 0v3.6h-4v9.75a2.65 2.65 0 0 0 .46 1.83 2.64 2.64 0 0 0 1.83.46h.87a7 7 0 0 0 .8-.1v4.17a13.07 13.07 0 0 1-1.52.15h-1.64a15.78 15.78 0 0 1-2.38-.17 5.57 5.57 0 0 1-2-.65 3.52 3.52 0 0 1-1.35-1.4 5 5 0 0 1-.4-2.42v-11.6h-3.3v-3.6H94v-5.9h5.4v5.9zm2.74-3.05v-4.45h5.4v4.45zm5.4 3v19.7h-5.4V31.73zm2.22 3.66v-3.6h3.22v-1.54a5.9 5.9 0 0 1 1.63-4.3 6.64 6.64 0 0 1 4.95-1.68c.48 0 1 0 1.45.06l1.4.1v4a16.67 16.67 0 0 0-2-.1 2.06 2.06 0 0 0-1.58.5 2.5 2.5 0 0 0-.48 1.74v1.18H126v3.6h-3.73V51.4h-5.4V35.33zm15.62-6.66v-4.45h5.4v4.45zm5.4 3v19.7h-5.4V31.73zm8.52 14.99a5.54 5.54 0 0 0 6.3.36A3.37 3.37 0 0 0 151 45.3h4.76a9 9 0 0 1-3.5 5.05 10.32 10.32 0 0 1-5.7 1.52 11.23 11.23 0 0 1-4.2-.74 8.66 8.66 0 0 1-3.15-2.13 9.5 9.5 0 0 1-2-3.27 12 12 0 0 1-.7-4.2 11.47 11.47 0 0 1 .73-4.1 9.5 9.5 0 0 1 5.23-5.43 10.17 10.17 0 0 1 4.1-.8 9.36 9.36 0 0 1 4.36 1 8.75 8.75 0 0 1 3.08 2.56 10.7 10.7 0 0 1 1.73 3.73 13.9 13.9 0 0 1 .38 4.36h-14.2a5.46 5.46 0 0 0 1.34 3.8zm6.1-10.35a3.8 3.8 0 0 0-2.95-1.06 4.72 4.72 0 0 0-2.16.44 4.1 4.1 0 0 0-1.37 1.08 4 4 0 0 0-.72 1.37 6.25 6.25 0 0 0-.25 1.3h8.8a6.2 6.2 0 0 0-1.35-3.1zm23.16 12.56a5.58 5.58 0 0 1-2.5 2.3 8.27 8.27 0 0 1-3.48.7 8.4 8.4 0 0 1-3.88-.86 7.78 7.78 0 0 1-2.76-2.3 10.47 10.47 0 0 1-1.64-3.38 14.15 14.15 0 0 1-.55-4 13.3 13.3 0 0 1 .54-3.82 9.93 9.93 0 0 1 1.64-3.25A8.2 8.2 0 0 1 162.6 32a8.1 8.1 0 0 1 3.8-.86 7.7 7.7 0 0 1 3.34.74 5.8 5.8 0 0 1 2.5 2.2h.07V24.2h5.4v27.16h-5.12v-2.5zm-.23-9.72a5.72 5.72 0 0 0-.78-2 4.4 4.4 0 0 0-1.44-1.4 4.18 4.18 0 0 0-2.2-.52 4.4 4.4 0 0 0-2.26.53 4.24 4.24 0 0 0-1.46 1.4 6.2 6.2 0 0 0-.8 2 10.75 10.75 0 0 0-.24 2.35 10.13 10.13 0 0 0 .26 2.3 6.3 6.3 0 0 0 .86 2 4.7 4.7 0 0 0 1.48 1.43 4 4 0 0 0 2.15.55 4.27 4.27 0 0 0 2.23-.53 4 4 0 0 0 1.43-1.43 6.38 6.38 0 0 0 .77-2 11.84 11.84 0 0 0 .23-2.36 11.57 11.57 0 0 0-.23-2.3zM70.05 95.56h31.5c7.65 0 17.74 0 23 3.16a20.6 20.6 0 0 1 10.2 18.1c0 9.25-4.86 16.18-12.88 19.46v.24c10.7 2.2 16.53 10.2 16.53 20.9 0 12.77-9.12 24.93-26.38 24.93H70.05zm11.55 37.32h23.1c13.14 0 18.5-4.74 18.5-13.86 0-12-8.52-13.74-18.5-13.74H81.6zm0 39.76h28.95c10 0 16.3-6.08 16.3-15.56 0-11.3-9.13-14.46-18.86-14.46H81.6z"
              />
              <path
                className={styles.BCorpSvg}
                d="M167.9 139a66 66 0 1 1-66-66 66 66 0 0 1 66 66zm-66-76.1a76.05 76.05 0 1 0 76.04 76.1 76.05 76.05 0 0 0-76.06-76.1zM39.87 231.43a4.16 4.16 0 0 0-4.2-3.37c-3.8 0-5.22 3.23-5.22 6.6 0 3.2 1.43 6.43 5.22 6.43 2.58 0 4-1.8 4.36-4.32h4.1c-.44 4.8-3.75 7.86-8.46 7.86-5.95 0-9.45-4.44-9.45-10 0-5.7 3.5-10.15 9.45-10.15 4.23 0 7.78 2.47 8.3 6.9zm13.08-1.53c4.4 0 7.22 2.9 7.22 7.35s-2.83 7.32-7.22 7.32-7.2-2.9-7.2-7.32 2.83-7.35 7.2-7.35zm0 11.8c2.6 0 3.4-2.25 3.4-4.45s-.8-4.47-3.4-4.47-3.37 2.24-3.37 4.47.78 4.44 3.37 4.44zm9.42-11.42H66v2.58h.06a4.92 4.92 0 0 1 4.45-3 2.9 2.9 0 0 1 .85.13v3.54a7.07 7.07 0 0 0-1.37-.13c-2.8 0-3.78 2-3.78 4.48v6.27h-3.83zm11.01 0H77v1.77h.05a4.63 4.63 0 0 1 4.14-2.15c4.36 0 6.32 3.53 6.32 7.48 0 3.72-2 7.2-6.1 7.2a4.94 4.94 0 0 1-4.18-2.1h-.04v6.6h-3.82zm10.34 7c0-2.2-.9-4.5-3.34-4.5s-3.3 2.24-3.3 4.5.85 4.4 3.33 4.4 3.32-2.14 3.32-4.4zm12.86-7.38c4.4 0 7.22 2.9 7.22 7.35s-2.83 7.32-7.22 7.32-7.2-2.9-7.2-7.32 2.84-7.35 7.2-7.35zm0 11.8c2.6 0 3.4-2.25 3.4-4.45s-.8-4.47-3.4-4.47-3.36 2.24-3.36 4.47.78 4.44 3.36 4.44zm9.7-11.42h3.64v2.58h.08a4.9 4.9 0 0 1 4.44-3 2.87 2.87 0 0 1 .84.13v3.54a7 7 0 0 0-1.37-.13c-2.8 0-3.76 2-3.76 4.48v6.27h-3.82zm10.32 4.28c.2-3.58 3.4-4.66 6.53-4.66 2.77 0 6.1.62 6.1 4v7.24a7.05 7.05 0 0 0 .5 3.1h-3.88a6 6 0 0 1-.27-1.35 6.36 6.36 0 0 1-4.68 1.7c-2.64 0-4.74-1.3-4.74-4.17 0-3.15 2.37-3.9 4.74-4.22s4.53-.26 4.53-1.82-1.14-1.88-2.48-1.88-2.4.6-2.53 2.1zm8.83 2.82c-.65.57-2 .6-3.18.8s-2.26.66-2.26 2 1.1 1.78 2.33 1.78a2.84 2.84 0 0 0 3.1-3.2zm11.62-7.1h2.8v2.56h-2.8v6.9c0 1.3.32 1.6 1.6 1.6a6.18 6.18 0 0 0 1.2-.1v3a14.5 14.5 0 0 1-2.24.15c-2.33 0-4.37-.55-4.37-3.32v-8.2h-2.32v-2.57h2.33v-4.2h3.82zm8.57-2.16h-3.82V225h3.82zm-3.82 2.16h3.82v13.92h-3.82zm13.05-.38c4.4 0 7.22 2.9 7.22 7.35s-2.83 7.32-7.22 7.32-7.18-2.9-7.18-7.32 2.82-7.35 7.18-7.35zm0 11.8c2.62 0 3.4-2.25 3.4-4.45s-.78-4.47-3.4-4.47-3.36 2.24-3.36 4.47.76 4.44 3.35 4.44zm9.69-11.42h3.64v1.94h.08a5 5 0 0 1 4.26-2.32c4.07 0 5.1 2.3 5.1 5.73v8.56h-3.83v-7.87c0-2.3-.68-3.42-2.46-3.42-2.08 0-3 1.17-3 4v7.3h-3.82zM26.1 256.04h151.62v9.7H26.12zm155.25-28.58a2 2 0 1 1-2.8-2.82 1.9 1.9 0 0 1 1.4-.58 2 2 0 0 1 1.4 3.4zm-2.62-2.63a1.66 1.66 0 0 0-.5 1.2 1.68 1.68 0 0 0 .5 1.23 1.7 1.7 0 0 0 2.43 0 1.67 1.67 0 0 0 .5-1.22 1.7 1.7 0 0 0-2.92-1.2zm1.17.1a1.72 1.72 0 0 1 .6.1.54.54 0 0 1 .34.54.48.48 0 0 1-.2.43.82.82 0 0 1-.33.1.54.54 0 0 1 .4.22.6.6 0 0 1 .1.34v.16a1.55 1.55 0 0 0 0 .16.3.3 0 0 0 0 .1h-.35v-.28a.53.53 0 0 0-.2-.5 1 1 0 0 0-.44-.07h-.3v.86h-.38v-2.2zm.42.36a1 1 0 0 0-.47-.1h-.33v.8h.35a1.08 1.08 0 0 0 .37 0 .4.4 0 0 0 .08-.66z"
              />
            </svg>
          </a>
        </div>

        <div className={styles.Legal}>
          <div className={styles.MWLogo}>
            <Image
              src="/static/svg/MW_Mark.svg"
              width={28}
              height={28}
              alt={TXT.eventsA11y.mwLogo}
              unoptimized
            />
          </div>
          {hasZaraz && (
            <span className={styles.Privacy} data-testid="privacy-choices-link">
              <Image width={28} height={28} src="/static/svg/privacy-icon.svg" alt={TXT.eventsA11y.privacyIcon} />
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  handlePrivacyOnClick(e.nativeEvent)
                }}
                id="zaraz-consent"
                className="underline"
              >
                {TXT.footer.yourPrivacyChoices}
              </a>
            </span>
          )}
          <span>
            ©
            {new Date().getFullYear()}
            {' '}
            {TXT.footer.mwAllRightsReserved}
            {' '}
          </span>
          <a suppressHydrationWarning href="https://meowwolf.com/terms-of-use" rel="noopener noreferrer">
            {TXT.footer.termsOfUse}
            {' '}
          </a>
          <a suppressHydrationWarning href="https://meowwolf.com/privacy-policy" rel="noopener noreferrer">
            {TXT.footer.privacyPolicy}
          </a>
        </div>
      </footer>
    </>
  )
}

export default Footer
